import type { PostgrestError } from '@supabase/supabase-js';

type Page<T> = PromiseLike<{ data: T[] | null; error: PostgrestError | null }>;

/** PostgREST returns at most 1000 rows per request; anything past that is silently dropped. */
const PAGE_SIZE = 1000;

/**
 * Long `.in()` lists go in the URL, and the API gateway answers 400 once it passes ~16 KB
 * (about 400 UUIDs). 150 ids keeps each request near 6 KB.
 */
const IN_CHUNK = 150;

/**
 * Fetch every row of a query, a page at a time. `page` must build a fresh query each call,
 * with a stable `.order()` so pages don't overlap, and end in `.range(from, to)`.
 */
export async function fetchAllRows<T>(page: (from: number, to: number) => Page<T>): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < PAGE_SIZE) return rows;
  }
}

/** Same as fetchAllRows, for a query filtered by `.in(column, values)` over a long list. */
export async function fetchAllRowsIn<T>(
  values: string[],
  page: (chunk: string[], from: number, to: number) => Page<T>,
): Promise<T[]> {
  const unique = [...new Set(values)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += IN_CHUNK) chunks.push(unique.slice(i, i + IN_CHUNK));
  const results = await Promise.all(
    chunks.map(chunk => fetchAllRows<T>((from, to) => page(chunk, from, to))),
  );
  return results.flat();
}
